import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  Icon,
  Button,
  UtilityInlineGrid,
  DropdownSearchSelect,
  DropdownMultiSelect,
} from 'rhinostyle';
import ReportModal from './ReportModal';
import {
  PAST_APPOINTMENT_OPTIONS,
} from '../constants/AppointmentManagerConstants';
import { calculatePastDateRange } from '../helpers/AppointmentHelpers';
import { REPORTS_TYPE_OPTIONS, FORM_TYPE_OPTIONS, USER_TYPE_OPTIONS, CONTACT_TYPE_OPTIONS, CONTACT_TYPE_OPTION_IDS, STATUS_OPTIONS } from '../constants/ReportConstants';
import { getKeyedFormTemplates } from '../selectors/formSelectors';
import { useGetReportMutation } from '../services/reportService';
import NotificationService from '../services/NotificationService';
import TagsDropdown from './TagsDropdown';
import CustomRolesDropdown from './CustomRolesDropdown';
import SystemRolesDropdown from './SystemRolesDropdown';
import GroupsDropdown from './GroupsDropdown';

const ReportFilters = (props) => {
  const {
    handleChange,
    filters,
  } = props;

  const pastMaxDate = moment().subtract(1, 'year').subtract(1, 'days').startOf('day');
  const now = moment();
  const [runReport] = useGetReportMutation();
  const formTemplates = useSelector((state) => getKeyedFormTemplates(state));
  const formTemplateIds = Object.keys(formTemplates);
  const [isOpen, setIsOpen] = useState(false);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [formTemplateItems, setFormTemplateItems] = useState(formTemplates);
  const [formTemplateItemIds, setFormTemplateItemIds] = useState(formTemplateIds);
  const [isReportLoading, setIsReportLoading] = useState(false);

  useEffect(() => {
    setFormTemplateItems(formTemplates);
    setFormTemplateItemIds(Object.keys(formTemplates));
  }, [formTemplates]);

  const contactTypeItems = CONTACT_TYPE_OPTIONS;
  const contactTypeItemIds = CONTACT_TYPE_OPTION_IDS;

  async function selectDate(date) {
    let { startDate, endDate } = date;
    if (date.activeKey === 5) {
      startDate = moment(startDate).startOf('day');
      endDate = moment(endDate).endOf('day');
    }
    handleChange({ activeKey: date.activeKey, startDate, endDate });
  }

  function handleToggleModal(value) {
    setIsOpen(value);
  }

  const renderDateView = () => (
    <div data-cypress="datePicker">
      <DateRangeDropdown
        position="left"
        name="Prescription Date Picker"
        startDate={moment(filters.startDate)}
        selectDate={selectDate}
        selectDateRange={calculatePastDateRange}
        dateLabelFormat="MMM D, YYYY"
        dropdownMenuItems={PAST_APPOINTMENT_OPTIONS}
        endDate={moment(filters.endDate)}
        minDate={pastMaxDate}
        maxDate={now}
        activeKey={filters.activeKey}
        isCustomDate
        key="past"
      />
    </div>
  );

  const fetchAllItems = (searchText) => {
    setItemSearchLoading(true);
    let searchedItems = {};
    if (searchText.length > 0) {
      // eslint-disable-next-line array-callback-return
      Object.keys(formTemplateItems).map((k) => {
        if ((formTemplateItems[k].title.toLowerCase()).includes(searchText.toLowerCase())) {
          searchedItems[k] = formTemplateItems[k];
        }
      });
    } else {
      searchedItems = formTemplateItems;
    }
    setItemSearchLoading(false);
    setFormTemplateItems(searchedItems);
    setFormTemplateItemIds(Object.keys(searchedItems));
  };

  const handleContactTypeSelect = (name, value) => {
    const newSelectedContactTypeIds = [...filters.selectedContactTypeIds];
    if (newSelectedContactTypeIds.includes(value)) {
      const index = newSelectedContactTypeIds.indexOf(value);
      if (index > -1) {
        newSelectedContactTypeIds.splice(index, 1);
      }
    } else {
      newSelectedContactTypeIds.push(value);
    }
    handleChange({ selectedContactTypeIds: newSelectedContactTypeIds });
  };

  const handleRunReport = async () => {
    const res = window.confirm( // eslint-disable-line no-alert
      'While this file is encrypted during transmission, once downloaded, you are responsible for implementing appropriate safeguards to protect the data, including encryption or deletion to the extent required by applicable law.', // eslint-disable-line max-len
    );

    if (res === false) {
      return;
    }

    const {
      reportType,
      startDate,
      endDate,
      formType,
      selectedFormTemplateId,
      selectedFormTemplateLabel,
      userType,
      selectedContactTypeIds,
      tagIds,
      groups,
      customRoles,
      systemRoles,
      status,
    } = filters;

    const payload = {
      reportType,
      fromDate: startDate,
      toDate: endDate,
      formType,
      formTemplateId: selectedFormTemplateId,
      formTemplate: selectedFormTemplateLabel,
      userType,
      selectedContactTypeIds: userType === 'member' ? '19' : selectedContactTypeIds.join(','),
      tagIds: tagIds.join(','),
      groups: groups.join(','),
      customRoles: customRoles.join(','),
      systemRoles: systemRoles.join(','),
      tagArr: tagIds || [],
      customRolesArr: customRoles || [],
      systemRolesArr: systemRoles || [],
      groupsArr: groups || [],
      status,
    };

    try {
      setIsReportLoading(true);
      const response = await runReport(payload).unwrap();
      setIsReportLoading(false);
      if (response && response.status === 'noData') {
        NotificationService('reportNoData', { status: 200 });
      }
      if (response && response.status === 'success') {
        const { presignedUrl } = response;

        window.open(presignedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error running report:', error);
      NotificationService('report', error);
      setIsReportLoading(false);
    }
  };

  const runAndDownloadReport = () => (
    <>
      <Button
        type="link"
        className="u-font-weight-bold"
        onClick={handleRunReport}
        loading={isReportLoading}
      >
        <Icon size="large" className="u-m-r-small" icon="run-report" />GENERATE REPORT
      </Button>
    </>
  );

  return (
    <>
      <div className="u-flex u-flex-direction-row u-flex-align-items-center">
        <UtilityInlineGrid>
          <Dropdown
            position="bottom"
            activeKey={filters.reportType}
            label="Report Type"
            name="reportType"
            onSelect={(reportType) => handleChange({ reportType })}
            dataCypress="reportTypeFilter"
          >
            {REPORTS_TYPE_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.id}
                id={option.id}
                label={option.value ? option.value : null}
              />
            ))}
          </Dropdown>
          {filters.reportType === 'form' && (
            <>
              {renderDateView()}
              <Dropdown
                position="bottom"
                activeKey={filters.formType}
                label="Form Type"
                name="formType"
                onSelect={(formType) => handleChange({ formType })}
                dataCypress="formTypeFilter"
              >
                {FORM_TYPE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    id={option.id}
                    label={option.value ? option.value : null}
                  />
                ))}
              </Dropdown>
              {filters.formType !== '' && (
                <div>
                  <DropdownSearchSelect
                    handleUpdateSelectedId={(
                      selectedId,
                      selectedItem,
                    ) => handleChange({
                      selectedFormTemplate: selectedItem,
                      selectedFormTemplateId: selectedId,
                      selectedFormTemplateLabel: selectedItem.title,
                    })}
                    itemSearchLoading={itemSearchLoading}
                    items={formTemplateItems}
                    itemsIds={formTemplateItemIds}
                    selectedItem={filters.selectedFormTemplate}
                    selectedItemId={filters.selectedFormTemplateId}
                    fetchAllItems={fetchAllItems}
                    dropdownLabel={filters.selectedFormTemplateLabel}
                    filterName="Form Template"
                    type="member"
                    className="multi-select__dropdown--fixed-height"
                  />
                </div>
              )}
            </>
          )}
          {filters.reportType === 'user' && (
            <>
              <Dropdown
                position="bottom"
                activeKey={filters.formType}
                label="User Type"
                name="userType"
                onSelect={(userType) => handleChange({ userType })}
                dataCypress="userTypeFilter"
              >
                {USER_TYPE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    id={option.id}
                    label={option.value ? option.value : null}
                  />
                ))}
              </Dropdown>
              {filters.userType === 'contact' && (
                <>
                  <DropdownMultiSelect
                    items={contactTypeItems}
                    itemIds={contactTypeItemIds}
                    selectedItemIds={filters.selectedContactTypeIds}
                    handleSelect={handleContactTypeSelect}
                    label="Contact Type"
                    name="contact"
                    getItemLabelValue={(item) => item?.value}
                    wide
                  />
                  <TagsDropdown
                    handleChange={(name, values) => handleChange({ [name]: values })}
                    value={filters.tagIds || []}
                  />
                </>

              )}
              {filters.userType === 'member' && (
                <>
                  <Dropdown
                    position="bottom"
                    activeKey={filters.memberStatus}
                    label="Status"
                    name="status"
                    onSelect={(status) => handleChange({ status })}
                    dataCypress="statusFilter"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <DropdownMenuItem
                        key={option.id}
                        id={option.id}
                        label={option.value ? option.value : null}
                      />
                    ))}
                  </Dropdown>
                  <TagsDropdown
                    handleChange={(name, values) => handleChange({ [name]: values })}
                    value={filters.tagIds || []}
                  />
                  <CustomRolesDropdown
                    handleChange={(name, values) => handleChange({ [name]: values })}
                    value={filters.customRoles || []}
                  />
                  <SystemRolesDropdown
                    handleChange={(name, values) => handleChange({ [name]: values })}
                    value={filters.systemRoles || []}
                  />
                  <GroupsDropdown
                    handleChange={(name, values) => handleChange({ [name]: values })}
                    value={filters.groups || []}
                  />
                </>

              )}
            </>
          )}
        </UtilityInlineGrid>
      </div>
      {filters.reportType !== '' && runAndDownloadReport()}
      <ReportModal isOpen={isOpen} handleToggleModal={handleToggleModal} />
    </>
  );
};

ReportFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  handleChange: PropTypes.func,
};

export default ReportFilters;
